<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{
              name: 'DashboardHome',
            }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>全系統管理</b-breadcrumb-item>
            <b-breadcrumb-item active>組織管理</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">組織管理</h4>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <div class="col-12 col-xl-3">
          <b-form-select
            v-model="query.is_enabled"
            :options="statusOptions"
            @change="handlePageResetWithoutReload"
          ></b-form-select>
        </div>
        <div
          class="col-12 col-xl-6 d-flex flex-column flex-xl-row align-items-start align-items-xl-center"
        >
          <b-button
            v-if="checkPermission([consts.ORGANIZATION_MODIFY])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="handleCreate"
          >
            <i class="fa fa-plus"></i>新增組織
          </b-button>
          <b-input-group>
            <b-form-input placeholder="搜尋組織名稱或組織編號" v-model="query.keyword" @keyup.enter="handlePageResetWithoutReload"></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-table striped hover responsive :items="organizations" :fields="fields" :busy="isLoading">
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="checkPermission([consts.MERCHANT_LIST_VIEW])"
            class="ml-2"
            variant="inverse-success"
            :to="{
              name: 'OrgMerchantList',
              params: {
                orgId: data.item.id,
              },
            }"
          ><span class="mdi mdi-lan"></span> 通路清單</b-button>

          <b-button
            v-if="checkPermission([consts.ROLE_LIST_VIEW])"
            class="ml-2"
            variant="inverse-info"
            :to="{
              name: 'OrgRoleList',
              params: {
                orgId: data.item.id,
              },
            }"
          ><span class="mdi mdi-account-star"></span> 角色管理</b-button>

          <b-button
            class="ml-2"
            variant="inverse-primary"
            :to="{
              name: 'OrganizationShow',
              params: {
                orgId: data.item.id,
              },
            }"
          ><span class="mdi mdi-eye"></span> 查看</b-button>

          <b-button
            v-if="checkPermission([consts.ORGANIZATION_MODIFY])"
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: 'OrganizationEdit',
              params: {
                orgId: data.item.id,
              },
            }"
          ><span class="mdi mdi-lead-pencil"></span> 編輯</b-button>
          <span :id="`delete-btn-${data.item.id}`" v-if="checkPermission([consts.ORGANIZATION_DELETE])">
            <b-button
              class="ml-2"
              variant="inverse-danger"
              :disabled="!canDelete(data.item)"
              @click="handleDelete(data.item.id, data.item.name, data.item.code)"
            ><span class="mdi mdi-delete"></span> 刪除</b-button>
          </span>
          <b-tooltip v-if="deleteTip(data.item)" :target="`delete-btn-${data.item.id}`" placement="right" boundary-padding="0">{{ deleteTip(data.item) }}</b-tooltip>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import organizationApi from "../../../apis/organization";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";

export default {
  components: { CustomPagination },
  mixins: [ paginationMixin ],
  data() {
    return {
      consts,
      isLoading: false,
      organizations: [],
      fields: [
        {
          key: "name",
          label: "組織名稱",
        },
        {
          key: "code",
          label: "組織代碼",
        },
        {
          key: "id",
          label: "組織編號",
        },
        {
          key: "is_enabled",
          label: "狀態",
          formatter: (value) => {
            return value ? '啟用' : '停用'
          },
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      statusOptions: [
        { value: '', text: '全部組織' },
        { value: 1, text: '啟用中組織' },
        { value: 0, text: '已停用組織' },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        is_enabled: 1,
      },
      total: 0,
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchOrganizations();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    if (this.query.is_enabled === "1") {
      this.query.is_enabled = 1;
    } else if (this.query.is_enabled === "0") {
      this.query.is_enabled = 0;
    } else if (this.query.is_enabled !== 0 && this.query.is_enabled !== 1) {
      this.query.is_enabled = '';
    }
  },
  async mounted() {
    await this.fetchOrganizations();
    this.initialized = true;
  },
  methods: {
    async fetchOrganizations() {
      try {
        this.isLoading = true;
        let params = {...this.query}
        if (params.is_enabled === '') {
          delete params.is_enabled;
        }
        const { data } = await organizationApi.getOrganizations(params);
        this.total = data.meta.total;
        this.organizations = data.data;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    async handleDelete(orgId, name, code) {
      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>組織名稱：${name}</div>
                <div>組織編號：${code}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await organizationApi.deleteOrganization(orgId);
              await this.fetchOrganizations();
            } catch (error) {
              console.log("");
            }
            this.isLoading = false;
          }
        });
    },
    handleCreate() {
      this.$router.push({
        name: "OrganizationCreate",
      });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    canDelete(item) {
      return item.is_enabled === 0 && item.merchants.length === 0;
    },
    deleteTip(item) {
      let tip;
      if (item.is_enabled) {
        tip = "無法刪除啟用中的組織";
      } else if (item.merchants.length !== 0) {
        tip = "組織中仍有通路，無法刪除組織";
      }
      return tip
    }
  },
};
</script>
